import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Spin, Empty } from 'antd';
import { type CheckboxChangeEvent } from 'antd/es/checkbox';
import { FolderOutlined } from '@ant-design/icons';
import { type ITopic } from '../../../../types/topic';
import { useGetSubjectContent } from '../../../../services/content';
import { type IContent } from '../../../../types/content';

import './MassiveEnrollmentModal.scss';

// Obtener carpetas únicas del contenido
const getUniqueFolders = (content: IContent[]): string[] => {
    const folders = content.reduce((acc: string[], item) => {
        if (item.folder && !acc.includes(`${(item.topic as any)._id!}-${item.folder.trim()}`)) {
            acc.push(`${(item.topic as any)._id!}-${item.folder.trim()}`);
        }

        return acc;
    }, []);

    return folders;
};

interface MassiveEnrollmentModalProps {
    open: boolean
    onCancel: () => void
    onOk: (selectedFolders?: string[]) => void
    loading: boolean
    selectedTopic?: ITopic
}

const MassiveEnrollmentModal: React.FC<MassiveEnrollmentModalProps> = ({
    open,
    onCancel,
    onOk,
    loading,
    selectedTopic
}) => {
    const [showFolders, setShowFolders] = useState(false);
    const [folders, setFolders] = useState<string[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<string[]>([]);

    const { getContent, loading: loadingContent, contents } = useGetSubjectContent();

    // Resetear el estado cuando se abre/cierra el modal
    useEffect(() => {
        if (!open) {
            setShowFolders(false);
            setSelectedFolders([]);
        }
    }, [open]);

    // Cuando cambia el tema seleccionado y showFolders es true, obtener el contenido
    useEffect(() => {
        if (showFolders && selectedTopic?.subject) {
            const subjectId = typeof selectedTopic.subject === 'string'
                ? selectedTopic.subject
                : selectedTopic.subject?._id;

            if (subjectId) {
                getContent({ variables: { input: subjectId } });
            }
        }
    }, [showFolders, selectedTopic, getContent]);

    // Actualizar carpetas cuando cambia el contenido
    useEffect(() => {
        if (contents && contents.length > 0) {
            const topicContent = contents.filter(content => content.topic === selectedTopic?._id);
            const uniqueFolders = getUniqueFolders(topicContent);
            setFolders(uniqueFolders);
        }
    }, [contents]);

    const handleToggleFolder = (folder: string) => {
        setSelectedFolders(prev => {
            if (prev.includes(folder)) {
                return prev.filter(f => f !== folder);
            } else {
                return [...prev, folder];
            }
        });
    };

    const handleFolderCheckboxChange = (e: CheckboxChangeEvent) => {
        setShowFolders(e.target.checked);
        if (!e.target.checked) {
            setSelectedFolders([]);
        }
    };

    // Manejador para enviar las carpetas seleccionadas
    const handleOk = () => {
        // Aqui tengo que pasar LAS QUE NO ESTAN SELECCIONADAS
        onOk(showFolders ? folders.filter(folder => !selectedFolders.includes(folder)) : undefined);
    };

    const handleSelectAll = () => {
        if (selectedFolders.length === folders.length) {
            // Si todas están seleccionadas, deseleccionamos todas
            setSelectedFolders([]);
        } else {
            // Si no todas están seleccionadas, seleccionamos todas
            setSelectedFolders([...folders]);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            okButtonProps={{
                loading
            }}
            onOk={handleOk}
            okText="Continuar"
            title="Matriculación masiva"
        >
            <div className='massive-modal-content'>
                <p>{`Vas a matricular a todos los usuarios al tema "${selectedTopic?.name}". ¿Estás seguro de que quieres continuar?`}</p>

                <Checkbox onChange={handleFolderCheckboxChange} checked={showFolders}>
                    Seleccionar carpetas
                </Checkbox>

                {showFolders && (
                    <div className="folders-container">
                        {loadingContent
                            ? (
                                <div className="folders-loading">
                                    <Spin size="small" />
                                    <span>Cargando carpetas...</span>
                                </div>
                            )
                            : folders.length > 0
                                ? (
                                    <>
                                        <div className="folders-header">
                                            <Checkbox
                                                indeterminate={selectedFolders.length > 0 && selectedFolders.length < folders.length}
                                                checked={folders.length > 0 && selectedFolders.length === folders.length}
                                                onChange={handleSelectAll}
                                            >
                                                <span className="select-all-text">Seleccionar todas</span>
                                            </Checkbox>
                                        </div>
                                        <div className="folders-list">
                                            {folders.map(folder => (
                                                <div key={folder} className="folder-item">
                                                    <Checkbox
                                                        checked={selectedFolders.includes(folder)}
                                                        onChange={() => handleToggleFolder(folder)}
                                                    >
                                                        <div className="folder-name">
                                                            <FolderOutlined /> {folder.split('-')[1]}
                                                        </div>
                                                    </Checkbox>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )
                                : (
                                    <Empty
                                        description="No hay carpetas disponibles"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default MassiveEnrollmentModal;
